import { useTranslation } from 'react-i18next';
import PageHeader from '@casops/components/layout/page-header/PageHeader';
import StandardPage from '@casops/templates/standard-page/StandardPage';
import { QueryTag, TagList } from '@casops/store/api';
import { useCallback, useMemo } from 'react';
import { ColDef } from 'ag-grid-community';
import { CollectionView } from '@casops/components/layout/collection-view/CollectionView';
import { useTableData } from '@casops/shared/hooks/useTableData';
import CreateItemButton from '@casops/components/inputs/create-item-button/CreateItemButton';
import {
  ConfigurationGroupModel,
  useListConfigurationGroupsQuery,
} from '@casops/store/apis/configurationGroupsApi';
import { Permission } from '@casops/permissions';
import { SidePanel } from '@casops/store/slices/globalSlice';
import { ModelName } from '@casops/store/api';
import { useNavigate } from 'react-router-dom';
import { compatibleSoftwareValueGetter } from '@casops/components/tables/cells/compatible-software/CompatibleSoftwareCellRenderer';
import { compatibleDevicesValueGetter } from '@casops/components/tables/cells/compatible-devices/CompatibleDevicesCellRenderer';
import { CellBadgeListRenderer } from '@casops/components/tables/cells/CellBadgeListRenderer';

type Model = ConfigurationGroupModel;

const modelName = ModelName.ConfigurationGroup;
const sidePanel = SidePanel.ManageConfigurationGroup;
const permission = Permission.Admin;

const tagsToClearOnRefresh = [{ type: QueryTag.ConfigurationGroup, id: TagList }];

export function ConfigurationGroupsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const columnDefs = useMemo<ColDef<Model>[]>(() => {
    const columnDefs: ColDef<Model>[] = [
      { field: 'name', headerName: t('Fields.Name'), minWidth: 100 },
      {
        field: 'hw',
        headerName: t('Fields.CompatibleSoftware'),
        valueGetter: compatibleSoftwareValueGetter,
        cellRenderer: CellBadgeListRenderer,
        minWidth: 160, // Min required to fit the column header text.
      },
      {
        field: 'hw',
        headerName: t('Fields.CompatibleDevices'),
        valueGetter: compatibleDevicesValueGetter,
        cellRenderer: CellBadgeListRenderer,
        minWidth: 160, // Min required to fit the column header text.
      },
      { field: 'desc', headerName: t('Fields.Description'), minWidth: 300 },
      { field: 'totA', headerName: t('Fields.AssetCount'), minWidth: 80 },
    ];

    return columnDefs;
  }, [t]);

  const noResultsDescription = t('Model.Collection.NoItems', { modelName });

  const { data, ...requestRemainder } = useListConfigurationGroupsQuery(undefined, {
    refetchOnMountOrArgChange: 60,
  });

  const { hasData, noDataDescription } = useTableData<Model>({
    data,
    ...requestRemainder,
    noResultsDescription,
  });

  const onRowClick = useCallback(
    (data: Model) => {
      navigate(`/configuration/configuration-group/${data.id}`);
    },
    [navigate],
  );

  return (
    <StandardPage
      header={
        <PageHeader
          title={t('Model.Collection.Title', { modelName })}
          tagsToClearOnRefresh={tagsToClearOnRefresh}
        />
      }
    >
      <CollectionView<Model>
        sliceKey={modelName}
        modelName={modelName}
        columnDefs={columnDefs}
        rowData={data}
        hasData={hasData}
        noDataDescription={noDataDescription}
        onRowClick={onRowClick}
        actions={
          <CreateItemButton modelName={modelName} sidePanel={sidePanel} permission={permission} />
        }
      />
    </StandardPage>
  );
}

export default ConfigurationGroupsPage;
